import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailSubstructure } from "../../../../../http/hrail/types/substructures";
import { useHrailSubstructuresApi } from "../../../../../http/hrail/useSubstructures";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateSubstructureModalDialogButton from "./CreateSubstructureModalDialogButton";
import SubstructureForm from "./SubstructureForm";

type Props = {
  active?: boolean;
};

const Substructures: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "substructures" });
  const aborter = useRef(new AbortController());
  const substructureApi = useHrailSubstructuresApi();
  const [itemToEdit, setItemToEdit] = useState<HrailSubstructure>();
  const [itemToDelete, setItemToDelete] = useState<HrailSubstructure>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "substructures");
  const isActiveTab = active ?? true;
  const updateVisibility = async (substructure: HrailSubstructure, visibility: VisibilityScopeValue) => {
    try {
      await substructureApi.updateAdminHrailSubstructureVisibility(substructure.id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (substructure: HrailSubstructure, newOrder: number) => {
    try {
      await substructureApi.updateAdminHrailSubstructureOrder(substructure.id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const { accessesAction, accessesDialog } = useEntityAccesses<HrailSubstructure>("hrail.substructure");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = substructureApi.useAdminHrailSubstructures(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.substructures.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.substructures.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateSubstructureModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<HrailSubstructure>
        isTall={true}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.name}</th>
            <th align="right">{i18n.translation.common.minThickness}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(substructure) => (
          <>
            <Table.Td noWrap={false}>{substructure.name ?? ""}</Table.Td>
            <Table.Td align="right" noWrap={false}>
              {formatNullableNumber(substructure.minThicknessMm, i18n.translation.common.millimeters)}
            </Table.Td>
            <td align="center">
              <Visibility value={substructure.visibility} onChange={(value) => updateVisibility(substructure, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor
                order={substructure.order}
                onUpdate={(newOrder) => updateOrder(substructure, newOrder)}
              />
            </td>
          </>
        )}
        onRowClick={(substructure) => setItemToEdit(substructure)}
        rowActions={(substructure) => [
          getEditAction(i18n, () => setItemToEdit(substructure)),
          accessesAction(substructure),
          getDeleteAction(i18n, () => setItemToDelete(substructure), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.substructures.edit}>
          <SubstructureForm substructure={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.substructures.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                substructureApi
                  .deleteAdminHrailSubstructure(itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.common.deleteEntityQuestion(
            i18n.translation.substructures.delete,
            [itemToDelete.name ?? ""].filter(Boolean).join(" - ") || itemToDelete.id,
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

const formatNullableNumber = (value: number | null | undefined, suffix = "") =>
  typeof value === "number" ? String(value) + suffix : "";

export default Substructures;
